import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { io } from 'socket.io-client';
import { SERVER_DOMAIN } from '../constants/constants';
import { getPendingSummary } from '../apis/admin_notifications';
import { useNavigate } from 'react-router-dom';
import { FaIdCard, FaCheckCircle, FaMoneyBillWave, FaTimes } from 'react-icons/fa';

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
    const [counts, setCounts] = useState({ identity: 0, badge: 0, withdrawal: 0 });
    const [toasts, setToasts] = useState([]);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const navigate = useNavigate();

    const fetchSummary = useCallback(async () => {
        try {
            const data = await getPendingSummary();
            if (data.success) {
                setCounts(data.payload || { identity: 0, badge: 0, withdrawal: 0 });
            }
        } catch (error) {
            console.error('Failed to fetch pending summary', error);
        }
    }, []);

    const removeToast = useCallback((id) => {
        setToasts((prev) => prev.filter((t) => t.id !== id));
    }, []);

    const addToast = useCallback((notification) => {
        const id = Date.now();
        setToasts((prev) => [...prev, { ...notification, id }]);
        setTimeout(() => {
            removeToast(id);
        }, 5000);
    }, [removeToast]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;

        fetchSummary();

        const socket = io(SERVER_DOMAIN, {
            auth: { token },
            transports: ['websocket']
        });

        socket.on('connect', () => {
            console.log('Connected to Admin Socket');
        });

        socket.on('NEW_ADMIN_TASK', (data) => {
            console.log('New Admin Task Received:', data);
            addToast(data);
            // No need to fetchSummary here as UPDATE_ADMIN_SUMMARY will follow
        });

        socket.on('UPDATE_ADMIN_SUMMARY', (data) => {
            console.log('Admin Summary Update Received:', data);
            setCounts(data || { identity: 0, badge: 0, withdrawal: 0 });
        });

        return () => {
            socket.disconnect();
        };
    }, [fetchSummary, addToast]);

    const jumpToTask = (type) => {
        setIsDrawerOpen(false);
        switch (type) {
            case 'identity':
                navigate('/document_data?status=pending');
                break;
            case 'badge':
                navigate('/BadgeRequest?status=pending');
                break;
            case 'withdrawal':
                navigate('/withdrawel_request?status=Pending');
                break;
            default:
                break;
        }
    };

    const totalCount = Object.values(counts).reduce((a, b) => a + b, 0);

    return (
        <NotificationContext.Provider value={{ 
            counts, 
            totalCount, 
            toasts, 
            isDrawerOpen, 
            setIsDrawerOpen, 
            jumpToTask 
        }}>
            {children}
            <ToastContainer toasts={toasts} onJump={jumpToTask} onDismiss={removeToast} />
        </NotificationContext.Provider>
    );
};

const ToastContainer = ({ toasts, onJump, onDismiss }) => {
    const getConfig = (type) => {
        switch (type?.toLowerCase()) {
            case 'identity':
                return { 
                    color: '#28a745', 
                    icon: <FaIdCard />, 
                    label: 'IDENTITY VERIFICATION',
                    bg: 'rgba(40, 167, 69, 0.1)'
                };
            case 'badge':
                return { 
                    color: '#007bff', 
                    icon: <FaCheckCircle />, 
                    label: 'BADGE REQUEST',
                    bg: 'rgba(0, 123, 255, 0.1)'
                };
            case 'withdrawal':
                return { 
                    color: '#ffc107', 
                    icon: <FaMoneyBillWave />, 
                    label: 'WITHDRAWAL REQUEST',
                    bg: 'rgba(255, 193, 7, 0.1)'
                };
            default:
                return { 
                    color: '#6c757d', 
                    icon: <FaCheckCircle />, 
                    label: 'NEW REQUEST',
                    bg: 'rgba(108, 117, 125, 0.1)'
                };
        }
    };

    return (
        <div style={{
            position: 'fixed',
            top: '20px',
            right: '24px',
            zIndex: 10000,
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            pointerEvents: 'none'
        }}>
            {toasts.map((toast) => {
                const config = getConfig(toast.type);
                return (
                    <div 
                        key={toast.id} 
                        onClick={() => onJump(toast.type)}
                        style={{
                            padding: '16px',
                            background: '#ffffff',
                            color: '#1a1a1a',
                            borderRadius: '16px',
                            boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '16px',
                            animation: 'slideInToast 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards',
                            pointerEvents: 'auto',
                            minWidth: '320px',
                            maxWidth: '400px',
                            cursor: 'pointer',
                            position: 'relative',
                            overflow: 'hidden',
                            border: '1px solid rgba(0,0,0,0.05)'
                        }}
                    >
                        {/* Progress Bar Background */}
                        <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            height: '4px',
                            backgroundColor: config.color,
                            width: '100%',
                            opacity: 0.2
                        }} />
                        {/* Shifting Progress Bar */}
                        <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            height: '4px',
                            backgroundColor: config.color,
                            width: '100%',
                            animation: 'shrinkProgress 5s linear forwards'
                        }} />

                        {/* Icon Container */}
                        <div style={{
                            width: '48px',
                            height: '48px',
                            borderRadius: '12px',
                            backgroundColor: config.bg,
                            color: config.color,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '20px',
                            flexShrink: 0
                        }}>
                            {config.icon}
                        </div>

                        {/* Content */}
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', pr: '20px' }}>
                            <span style={{ 
                                fontSize: '11px', 
                                fontWeight: '800', 
                                color: config.color, 
                                letterSpacing: '0.5px',
                                marginBottom: '2px'
                            }}>
                                {config.label}
                            </span>
                            <span style={{ fontSize: '14px', fontWeight: '500', lineHeight: '1.4' }}>
                                {toast.message}
                            </span>
                        </div>

                        {/* Dismiss Button */}
                        <button 
                            onClick={(e) => {
                                e.stopPropagation();
                                onDismiss(toast.id);
                            }}
                            style={{
                                background: 'none',
                                border: 'none',
                                color: '#ccc',
                                cursor: 'pointer',
                                padding: '4px',
                                transition: 'color 0.2s',
                                alignSelf: 'flex-start'
                            }}
                            onMouseEnter={e => e.currentTarget.style.color = '#666'}
                            onMouseLeave={e => e.currentTarget.style.color = '#ccc'}
                        >
                            <FaTimes fontSize="14px" />
                        </button>
                    </div>
                );
            })}
            <style>{`
                @keyframes slideInToast {
                    from { transform: translateX(100%); opacity: 0; }
                    to { transform: translateX(0); opacity: 1; }
                }
                @keyframes shrinkProgress {
                    from { width: 100%; }
                    to { width: 0%; }
                }
            `}</style>
        </div>
    );
};
